import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { ProductSliderProps } from "@omni/types/components/props/productSlider.props";
import { BreakpointMinSizes } from "@omni/types/enums/breakPoint.enum";
import { productSlider } from "@omni/ui/brand/product/productSlider/productSlider.styles";
import { cn } from "@omni/utils/styles";
import { entries, values } from "lodash-es";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Swiper as SwiperCore } from "swiper/types";

import { ProductSliderContextProvider } from "@/components/Product/ProductSlider/context/productSlider.context";
import { RecentsProductslider } from "@/components/Product/ProductSlider/sources/recents/recents";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import {
    useBreakpoint,
    withBreakpoints
} from "@/utils/breakpoints/breakpoints.provider";
import { ProductCartContext } from "@/utils/hooks/cart.hook";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { getTagIdFor } from "@/utils/tagging/tag.utils";

import { AccessoriesProductSlider } from "./sources/accessories/accessories.component";
import { AlgoliaRecommendProductslider } from "./sources/algoliaRecommend/algoliaRecommend.component";
import { AlgoliaSearchProductSlider } from "./sources/algoliaSearch/algoliaSearch.component";
import { ControlledProductSlider } from "./sources/controlled/controlled.component";
import { MainProductSlider } from "./sources/mainProducts/mainProducts.component";
import { SkuProductSlider } from "./sources/sku/sku.component";

type InnerProps = ProductSliderProps & {
    children: ReactNode;
};

const ProductSlider = ({ children, ...props }: InnerProps) => {
    const dispatch = useThunkDispatch();

    const cart = useSelector(CartSelectors.getCart);
    const isLoadingCart = useSelector(CartSelectors.isLoadingCart);

    const [mainSwiper, setMainSwiper] = useState<SwiperCore | null>(null);
    const [allowPrev, setAllowPrev] = useState<boolean>(false);
    const [allowNext, setAllowNext] = useState<boolean>(false);
    const prevButtonRef = useRef<HTMLButtonElement | null>(null);
    const nextButtonRef = useRef<HTMLButtonElement | null>(null);

    const allowInstantAddToCart = props.productTileConfig.allowInstantAddToCart;

    useEffect(() => {
        if (!allowInstantAddToCart) return;
        if (isLoadingCart) return;

        dispatch(CartModule.load());
    }, [isLoadingCart, allowInstantAddToCart, dispatch]);

    return (
        <ProductCartContext
            value={{
                allowInstantAddToCart: allowInstantAddToCart,
                cart: cart
            }}
        >
            <ProductSliderContextProvider
                title={props.title || undefined}
                controlsPosition={props.controlsPosition}
                source={props.source}
                breakpointsConfig={props.breakpointsConfig}
                productTileConfig={props.productTileConfig}
                mainSwiper={mainSwiper}
                allowPrev={allowPrev}
                allowNext={allowNext}
                prevButtonRef={prevButtonRef}
                nextButtonRef={nextButtonRef}
                setMainSwiper={setMainSwiper}
                setAllowPrev={setAllowPrev}
                setAllowNext={setAllowNext}
            >
                {props.source === ProductSliderSources.Controlled && (
                    <ControlledProductSlider products={props.products}>
                        {children}
                    </ControlledProductSlider>
                )}
                {props.source === ProductSliderSources.HandPicked && (
                    <SkuProductSlider
                        skus={props.skus}
                        serverSideProducts={props.serverSideProducts}
                    >
                        {children}
                    </SkuProductSlider>
                )}
                {props.source === ProductSliderSources.Accessory && (
                    <AccessoriesProductSlider
                        sku={props.sku}
                        serverSideProducts={props.serverSideProducts}
                    >
                        {children}
                    </AccessoriesProductSlider>
                )}
                {props.source === ProductSliderSources.MainProduct && (
                    <MainProductSlider
                        sku={props.sku}
                        serverSideProducts={props.serverSideProducts}
                    >
                        {children}
                    </MainProductSlider>
                )}
                {props.source === ProductSliderSources.Recent && (
                    <RecentsProductslider
                        sku={props.sku}
                        serverSideProducts={props.serverSideProducts}
                    >
                        {children}
                    </RecentsProductslider>
                )}
                {props.source === ProductSliderSources.AlgoliaRecommend && (
                    <AlgoliaRecommendProductslider
                        algoliaRecommendParams={props.algoliaRecommendParams}
                        sku={props.sku}
                        serverSideProducts={props.serverSideProducts}
                    >
                        {children}
                    </AlgoliaRecommendProductslider>
                )}
                {props.source === ProductSliderSources.AlgoliaSearch && (
                    <AlgoliaSearchProductSlider
                        algoliaSearchParams={props.algoliaSearchParams}
                        serverSideProducts={props.serverSideProducts}
                    >
                        {children}
                    </AlgoliaSearchProductSlider>
                )}
            </ProductSliderContextProvider>
        </ProductCartContext>
    );
};

const RenderProductSliderOnBreakpointWrapper = withBreakpoints(
    ({ children, ...props }: InnerProps) => {
        const { breakpoints } = useBreakpoint();
        const isClientSide = useIsClientSide();

        const matchingBreakpoint =
            entries(breakpoints)
                .filter(([, matches]) => matches)
                .map(([breakpoint]) => breakpoint)
                .at(-1) ?? "xxs";

        const renderServer = !isClientSide;
        const renderClient =
            isClientSide &&
            Object.keys(breakpoints).length > 0 &&
            !props.breakpointsConfig.hidden.includes(
                matchingBreakpoint as BreakpointMinSizes
            );

        if (!renderServer && !renderClient) {
            return null;
        }

        return <ProductSlider {...props}>{children}</ProductSlider>;
    }
);

type Props = ProductSliderProps & {
    className?: string;
    children: ReactNode;
};

export const Base = ({
    breakpointsConfig: breakPointsConfig,
    className,
    children,
    ...props
}: Props) => {
    const breakpoints = values(BreakpointMinSizes).reduce<
        Partial<Record<BreakpointMinSizes, boolean>>
    >((acc, breakpoint) => {
        acc[breakpoint] = breakPointsConfig.hidden.includes(breakpoint);

        return acc;
    }, {});

    const { base } = productSlider();

    return (
        <div
            className={cn(
                base({
                    hiddenXs: breakpoints[BreakpointMinSizes.xs],
                    hiddenSm: breakpoints[BreakpointMinSizes.sm],
                    hiddenMd: breakpoints[BreakpointMinSizes.md],
                    hiddenLg: breakpoints[BreakpointMinSizes.lg],
                    hiddenXl: breakpoints[BreakpointMinSizes.xl],
                    isRecent: props.source === ProductSliderSources.Recent,
                    className: className
                })
            )}
            {...getTagIdFor("product-slider", props.source)}
        >
            <RenderProductSliderOnBreakpointWrapper
                {...props}
                breakpointsConfig={breakPointsConfig}
            >
                {children}
            </RenderProductSliderOnBreakpointWrapper>
        </div>
    );
};
